/**
 * Validation functions for the Wisdom Spirits platform.
 * Each validator returns a result with success status and field-level errors.
 */

import { FieldError } from './api-types';
import { BeverageFormat, CommissionType, FarmBillStatus, UpOrDown } from './enums';

/** Result returned by all validation functions. */
export interface ValidationResult {
  success: boolean;
  errors: FieldError[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function ok(): ValidationResult {
  return { success: true, errors: [] };
}

function fail(errors: FieldError[]): ValidationResult {
  return { success: false, errors };
}

function combine(results: ValidationResult[]): ValidationResult {
  const errors = results.flatMap((r) => r.errors);
  return errors.length === 0 ? ok() : fail(errors);
}

// ─── URL Validator ──────────────────────────────────────────────────────────

/**
 * Validates a URL string.
 * - Must be a valid URL (http or https scheme)
 * - Maximum 2048 characters
 *
 * Requirements: 13.10, 14.5
 */
export function validateUrl(url: string, fieldName = 'url'): ValidationResult {
  const errors: FieldError[] = [];

  if (!url || url.trim().length === 0) {
    errors.push({ field: fieldName, reason: 'required' });
    return fail(errors);
  }

  if (url.length > 2048) {
    errors.push({ field: fieldName, reason: 'exceeds_max_length_2048' });
  }

  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      errors.push({ field: fieldName, reason: 'invalid_url_scheme' });
    }
  } catch {
    errors.push({ field: fieldName, reason: 'invalid_url_format' });
  }

  return errors.length === 0 ? ok() : fail(errors);
}

// ─── Password Policy Validator ──────────────────────────────────────────────

/**
 * Validates a password against the platform policy.
 * - 8 to 128 characters
 * - At least 1 uppercase letter
 * - At least 1 lowercase letter
 * - At least 1 number
 *
 * Requirement: 5.5
 */
export function validatePassword(password: string): ValidationResult {
  const errors: FieldError[] = [];

  if (typeof password !== 'string' || password.length === 0) {
    errors.push({ field: 'password', reason: 'required' });
    return fail(errors);
  }

  if (password.length < 8) {
    errors.push({ field: 'password', reason: 'too_short_minimum_8' });
  }

  if (password.length > 128) {
    errors.push({ field: 'password', reason: 'too_long_maximum_128' });
  }

  if (!/[A-Z]/.test(password)) {
    errors.push({ field: 'password', reason: 'missing_uppercase_letter' });
  }

  if (!/[a-z]/.test(password)) {
    errors.push({ field: 'password', reason: 'missing_lowercase_letter' });
  }

  if (!/[0-9]/.test(password)) {
    errors.push({ field: 'password', reason: 'missing_number' });
  }

  return errors.length === 0 ? ok() : fail(errors);
}

// ─── Display Name Validator ─────────────────────────────────────────────────

/**
 * Validates a user display name.
 * - 3 to 50 characters
 *
 * Requirement: 5.1
 */
export function validateDisplayName(displayName: string): ValidationResult {
  const errors: FieldError[] = [];

  if (typeof displayName !== 'string' || displayName.trim().length === 0) {
    errors.push({ field: 'displayName', reason: 'required' });
    return fail(errors);
  }

  if (displayName.length < 3) {
    errors.push({ field: 'displayName', reason: 'too_short_minimum_3' });
  }

  if (displayName.length > 50) {
    errors.push({ field: 'displayName', reason: 'too_long_maximum_50' });
  }

  return errors.length === 0 ? ok() : fail(errors);
}

// ─── Review Text Validator ──────────────────────────────────────────────────

/**
 * Validates review text length.
 * - 10 to 2000 characters (counting all characters including spaces and punctuation)
 *
 * Requirement: 7.2
 */
export function validateReviewText(text: string): ValidationResult {
  const errors: FieldError[] = [];

  if (typeof text !== 'string' || text.length === 0) {
    errors.push({ field: 'reviewText', reason: 'required' });
    return fail(errors);
  }

  if (text.length < 10) {
    errors.push({ field: 'reviewText', reason: 'too_short_minimum_10' });
  }

  if (text.length > 2000) {
    errors.push({ field: 'reviewText', reason: 'too_long_maximum_2000' });
  }

  return errors.length === 0 ? ok() : fail(errors);
}

// ─── Rating Submission Validator ────────────────────────────────────────────

export interface RatingInput {
  ratingValue: number;
  buzzScore: number;
  upOrDown: string;
}

/**
 * Validates a rating submission.
 * - ratingValue: 1 to 5 in 0.5 increments
 * - buzzScore: 1 to 10, integer only
 * - upOrDown: must be 'Up', 'Down', or 'Neutral'
 *
 * Requirement: 6.7
 */
export function validateRating(input: RatingInput): ValidationResult {
  const errors: FieldError[] = [];

  // Rating value: 1–5 in 0.5 increments
  const { ratingValue, buzzScore, upOrDown } = input;

  if (ratingValue == null || typeof ratingValue !== 'number' || isNaN(ratingValue)) {
    errors.push({ field: 'ratingValue', reason: 'required' });
  } else if (ratingValue < 1 || ratingValue > 5) {
    errors.push({ field: 'ratingValue', reason: 'out_of_range_1_to_5' });
  } else if ((ratingValue * 2) % 1 !== 0) {
    errors.push({ field: 'ratingValue', reason: 'must_be_in_0.5_increments' });
  }

  // Buzz score: 1–10, integer
  if (buzzScore == null || typeof buzzScore !== 'number' || isNaN(buzzScore)) {
    errors.push({ field: 'buzzScore', reason: 'required' });
  } else if (!Number.isInteger(buzzScore)) {
    errors.push({ field: 'buzzScore', reason: 'must_be_integer' });
  } else if (buzzScore < 1 || buzzScore > 10) {
    errors.push({ field: 'buzzScore', reason: 'out_of_range_1_to_10' });
  }

  // Up or Down: enum value
  const validUpOrDown = Object.values(UpOrDown) as string[];
  if (!upOrDown || typeof upOrDown !== 'string') {
    errors.push({ field: 'upOrDown', reason: 'required' });
  } else if (!validUpOrDown.includes(upOrDown)) {
    errors.push({ field: 'upOrDown', reason: 'invalid_value_must_be_Up_Down_or_Neutral' });
  }

  return errors.length === 0 ? ok() : fail(errors);
}

// ─── Commission Value Range Validator ───────────────────────────────────────

export interface CommissionInput {
  commissionType: string;
  commissionValue: number;
}

/**
 * Validates commission value based on its type.
 * - percentage: 1–100%
 * - CPA: $0.01–$500.00
 * - revenue_split: 1–100%
 *
 * Requirement: 15.12
 */
export function validateCommissionValue(input: CommissionInput): ValidationResult {
  const errors: FieldError[] = [];
  const { commissionType, commissionValue } = input;

  const validTypes = Object.values(CommissionType) as string[];
  if (!commissionType || !validTypes.includes(commissionType)) {
    errors.push({ field: 'commissionType', reason: 'invalid_commission_type' });
    return fail(errors);
  }

  if (commissionValue == null || typeof commissionValue !== 'number' || isNaN(commissionValue)) {
    errors.push({ field: 'commissionValue', reason: 'required' });
    return fail(errors);
  }

  switch (commissionType) {
    case CommissionType.Percentage:
      if (commissionValue < 1 || commissionValue > 100) {
        errors.push({ field: 'commissionValue', reason: 'out_of_range_1_to_100_percent' });
      }
      break;
    case CommissionType.CPA:
      if (commissionValue < 0.01 || commissionValue > 500) {
        errors.push({ field: 'commissionValue', reason: 'out_of_range_0.01_to_500_dollars' });
      }
      break;
    case CommissionType.RevenueSplit:
      if (commissionValue < 1 || commissionValue > 100) {
        errors.push({ field: 'commissionValue', reason: 'out_of_range_1_to_100_percent' });
      }
      break;
  }

  return errors.length === 0 ? ok() : fail(errors);
}

// ─── Product Field Validator ────────────────────────────────────────────────

export interface ProductInput {
  brandName?: string;
  productName?: string;
  thcContentMg?: number;
  beverageFormat?: string;
  volumeOz?: number;
  servingSize?: string;
  sourceUrl?: string;
  farmBillStatus?: string;
  // Optional fields with constraints
  companyWebsiteUrl?: string;
  cbdContentMg?: number;
  caloriesPerServing?: number;
  sugarGrams?: number;
  flavorProfile?: string;
  priceUsd?: number;
  imageUrl?: string;
  onsetTimeMinutes?: number;
  buyUrl?: string;
}

/**
 * Validates product fields for creation/update.
 * Validates required fields, numeric ranges, string lengths, and enum values.
 *
 * Requirement: 2.5
 */
export function validateProduct(input: ProductInput): ValidationResult {
  const results: ValidationResult[] = [];
  const errors: FieldError[] = [];

  // Required: brandName (max 100)
  if (!input.brandName || input.brandName.trim().length === 0) {
    errors.push({ field: 'brandName', reason: 'required' });
  } else if (input.brandName.length > 100) {
    errors.push({ field: 'brandName', reason: 'exceeds_max_length_100' });
  }

  // Required: productName (max 200)
  if (!input.productName || input.productName.trim().length === 0) {
    errors.push({ field: 'productName', reason: 'required' });
  } else if (input.productName.length > 200) {
    errors.push({ field: 'productName', reason: 'exceeds_max_length_200' });
  }

  // Required: thcContentMg (0.00–100.00)
  if (input.thcContentMg == null || typeof input.thcContentMg !== 'number' || isNaN(input.thcContentMg)) {
    errors.push({ field: 'thcContentMg', reason: 'required' });
  } else if (input.thcContentMg < 0 || input.thcContentMg > 100) {
    errors.push({ field: 'thcContentMg', reason: 'out_of_range_0_to_100' });
  }

  // Required: beverageFormat (enum)
  const validFormats = Object.values(BeverageFormat) as string[];
  if (!input.beverageFormat) {
    errors.push({ field: 'beverageFormat', reason: 'required' });
  } else if (!validFormats.includes(input.beverageFormat)) {
    errors.push({ field: 'beverageFormat', reason: 'invalid_beverage_format' });
  }

  // Required: volumeOz (0.1–128.0)
  if (input.volumeOz == null || typeof input.volumeOz !== 'number' || isNaN(input.volumeOz)) {
    errors.push({ field: 'volumeOz', reason: 'required' });
  } else if (input.volumeOz < 0.1 || input.volumeOz > 128) {
    errors.push({ field: 'volumeOz', reason: 'out_of_range_0.1_to_128' });
  }

  // Required: servingSize
  if (!input.servingSize || input.servingSize.trim().length === 0) {
    errors.push({ field: 'servingSize', reason: 'required' });
  }

  // Required: sourceUrl (valid URL, max 2048)
  if (!input.sourceUrl || input.sourceUrl.trim().length === 0) {
    errors.push({ field: 'sourceUrl', reason: 'required' });
  } else {
    results.push(validateUrl(input.sourceUrl, 'sourceUrl'));
  }

  // Required: farmBillStatus (enum)
  const validStatuses = Object.values(FarmBillStatus) as string[];
  if (!input.farmBillStatus) {
    errors.push({ field: 'farmBillStatus', reason: 'required' });
  } else if (!validStatuses.includes(input.farmBillStatus)) {
    errors.push({ field: 'farmBillStatus', reason: 'invalid_farm_bill_status' });
  }

  // Optional: companyWebsiteUrl (valid URL, max 2048)
  if (input.companyWebsiteUrl != null && input.companyWebsiteUrl.length > 0) {
    results.push(validateUrl(input.companyWebsiteUrl, 'companyWebsiteUrl'));
  }

  // Optional: cbdContentMg (0.00–500.00)
  if (input.cbdContentMg != null) {
    if (typeof input.cbdContentMg !== 'number' || isNaN(input.cbdContentMg)) {
      errors.push({ field: 'cbdContentMg', reason: 'must_be_number' });
    } else if (input.cbdContentMg < 0 || input.cbdContentMg > 500) {
      errors.push({ field: 'cbdContentMg', reason: 'out_of_range_0_to_500' });
    }
  }

  // Optional: flavorProfile (max 200)
  if (input.flavorProfile != null && input.flavorProfile.length > 200) {
    errors.push({ field: 'flavorProfile', reason: 'exceeds_max_length_200' });
  }

  // Optional: priceUsd (0.01–999.99)
  if (input.priceUsd != null) {
    if (typeof input.priceUsd !== 'number' || isNaN(input.priceUsd)) {
      errors.push({ field: 'priceUsd', reason: 'must_be_number' });
    } else if (input.priceUsd < 0.01 || input.priceUsd > 999.99) {
      errors.push({ field: 'priceUsd', reason: 'out_of_range_0.01_to_999.99' });
    }
  }

  // Optional: imageUrl (valid URL, max 2048)
  if (input.imageUrl != null && input.imageUrl.length > 0) {
    results.push(validateUrl(input.imageUrl, 'imageUrl'));
  }

  // Optional: onsetTimeMinutes (integer 5–120)
  if (input.onsetTimeMinutes != null) {
    if (typeof input.onsetTimeMinutes !== 'number' || isNaN(input.onsetTimeMinutes)) {
      errors.push({ field: 'onsetTimeMinutes', reason: 'must_be_number' });
    } else if (!Number.isInteger(input.onsetTimeMinutes)) {
      errors.push({ field: 'onsetTimeMinutes', reason: 'must_be_integer' });
    } else if (input.onsetTimeMinutes < 5 || input.onsetTimeMinutes > 120) {
      errors.push({ field: 'onsetTimeMinutes', reason: 'out_of_range_5_to_120' });
    }
  }

  const combined = combine(results);
  const allErrors = [...errors, ...combined.errors];
  return allErrors.length === 0 ? ok() : fail(allErrors);
}
